import ProfileAvatar from '../components/ProfileAvatar';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import GetAppButton from '../components/GetAppButton';
import './Announcements.css';

const AnnouncementsPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    const fetchAnnouncements = useCallback(async (pageNum) => {
        try {
            const res = await fetch(`${API_BASE_URL}/announcements/all?limit=20&page=${pageNum}`);
            const data = await res.json();
            if (data.success) {
                setAnnouncements(prev => pageNum === 1 ? data.announcements : [...prev, ...data.announcements]);
                setHasMore(data.announcements.length === 20);
            } else {
                setError("Failed to load announcements");
            }
        } catch (err) {
            console.error(err);
            setError("Network error");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        fetchAnnouncements(1);
    }, [fetchAnnouncements]);

    const lastAnnouncementElementRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLoadingMore(true);
                setPage(prevPage => {
                    const nextPage = prevPage + 1;
                    fetchAnnouncements(nextPage);
                    return nextPage;
                });
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore, fetchAnnouncements]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    };

    return (
        <div className="announcements-page">
            {/* Header */}
            <header className="announcements-header">
                <Link to="/" className="header-back-button">
                    <div className="back-icon-circle">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M10 19L3 12L10 5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3 12H15C18.3137 12 21 14.6863 21 18V19" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                </Link>

                <h1 className="header-title">Announcements</h1>

                <GetAppButton />
            </header>

            {/* Content */}
            <div className="announcements-content">
                {loading && page === 1 ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading updates...</p>
                    </div>
                ) : error ? (
                    <div className="error-container">{error}</div>
                ) : announcements.length === 0 ? (
                    <div className="empty-state-container">
                        <svg
                            width="80"
                            height="80"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ opacity: 0.3, marginBottom: '1.5rem', color: '#F6B520' }}
                        >
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                            <line x1="8" y1="21" x2="16" y2="21"></line>
                            <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        <h2 style={{ color: '#FFFFFF', marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: '600' }}>No Announcements Yet</h2>
                        <p style={{ color: '#B0B0B0', textAlign: 'center', maxWidth: '400px', lineHeight: '1.6' }}>
                            There are currently no announcements to display. Check back later for updates!
                        </p>
                    </div>
                ) : (
                    <div className="announcements-grid">
                        {announcements.map((ann, index) => {
                            const isLast = index === announcements.length - 1;
                            return (
                                <Link
                                    to={`/announcements/${ann._id}`}
                                    key={ann._id}
                                    className="announcement-card-link"
                                    ref={isLast ? lastAnnouncementElementRef : null}
                                >
                                    <div className="announcement-card">
                                        {/* Image Section */}
                                        <div className="card-image-container">
                                            {ann.imageUrl ? (
                                                <img
                                                    src={ann.thumbnailUrl || ann.imageUrl}
                                                    alt={ann.title}
                                                    className="card-image"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}

                                            {/* Fallback Icon */}
                                            <div className={`card-image-fallback ${ann.imageUrl ? 'hidden' : ''}`}>
                                                <svg
                                                    width="64"
                                                    height="64"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                    <polyline points="21 15 16 10 5 21"></polyline>
                                                </svg>
                                            </div>
                                        </div>

                                        <div className="card-content">
                                            <div className="card-tags">
                                                <span className={`category-tag category-${ann.category || 'general'}`}>
                                                    {ann.category || 'General'}
                                                </span>

                                                {ann.uni && (
                                                    <span className={`uni-tag uni-${ann.uni.toLowerCase()}`}>
                                                        {ann.uni}
                                                    </span>
                                                )}
                                                {(Array.isArray(ann.targetMajor) ? ann.targetMajor : ann.targetMajor ? [ann.targetMajor] : []).map(major => <span key={major} className="major-tag">{major}</span>)}
                                            </div>

                                            <h2 className="card-title">{ann.title}</h2>

                                            <p className="card-body">
                                                {ann.body}
                                            </p>

                                            <div className="card-footer">
                                                <div className="card-meta-info">
                                                    <ProfileAvatar avatar={ann.authorAvatar} /><span className="card-author">{ann.authorName || 'myGUNI Admin'}</span>
                                                    <span className="card-separator">•</span>
                                                    <span className="card-date">{formatDate(ann.createdAt)}</span>
                                                </div>
                                                <span className="card-read-more">Read More →</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                        {loadingMore && (
                            <div className="loading-more">
                                <div className="spinner-small"></div>
                                <span>Loading more...</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-links">
                    <Link to="/" className="footer-link">Home</Link>
                    <Link to="/announcements" className="footer-link">Announcements</Link>
                    <a href="/privacy-policy.html" className="footer-link">Privacy Policy</a>
                    <a href="/faq.html" className="footer-link">FAQ</a>
                </div>
                <div className="footer-copyright">
                    <p>&copy; 2026 myGUNI. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default AnnouncementsPage;
